import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { emailDigest } from '../../../utils/api';
import { useLanguage } from '../../../contexts/LanguageContext';
import { 
  Mail, 
  Send, 
  TrendingUp, 
  TrendingDown, 
  Package, 
  DollarSign,
  Eye,
  Bell,
  BellOff,
  CheckCircle2,
  Loader2,
  Calendar,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

export default function WeeklyDigestWidget() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [subscribed, setSubscribed] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    loadDigestPreview();
    loadPreferences();
  }, []);

  const loadDigestPreview = async () => {
    try {
      const res = await emailDigest.preview();
      setPreview(res.data);
    } catch (error) {
      console.error('Failed to load digest preview:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPreferences = async () => {
    try {
      const res = await emailDigest.getPreferences();
      setSubscribed(res.data.preferences?.weekly_digest !== false);
    } catch (error) {
      console.error('Failed to load preferences:', error);
    }
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      const res = await emailDigest.send();
      toast.success(t('digest.sent_success', 'Weekly digest sent to your email!'), {
        icon: <CheckCircle2 className="w-4 h-4 text-green-500" />
      });
    } catch (error) {
      toast.error(t('digest.sent_error', 'Failed to send digest'));
    } finally {
      setSending(false);
    }
  };

  const handleToggleSubscription = async () => {
    try {
      if (subscribed) {
        await emailDigest.unsubscribe();
        setSubscribed(false);
        toast.info(t('digest.unsubscribed', 'Unsubscribed from weekly digest'));
      } else {
        await emailDigest.subscribe();
        setSubscribed(true);
        toast.success(t('digest.subscribed', 'Subscribed to weekly digest'));
      }
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  const getTrendIcon = (percent) => {
    if (percent > 0) return <TrendingUp className="w-3 h-3 text-green-500" />;
    if (percent < 0) return <TrendingDown className="w-3 h-3 text-red-500" />;
    return null;
  };

  const formatTrend = (percent) => {
    if (percent === 0) return '';
    const sign = percent > 0 ? '+' : '';
    return `${sign}${percent}%`;
  };

  if (loading) {
    return (
      <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <div className="bg-purple-100 p-1.5 rounded-lg">
              <Mail className="w-4 h-4 text-purple-600" />
            </div>
            <span className="text-purple-900">
              {t('digest.title', 'Weekly Digest')}
            </span>
          </CardTitle>
          <div 
            className="flex items-center gap-2 cursor-pointer"
            onClick={handleToggleSubscription}
            data-testid="digest-subscription-toggle"
          >
            {subscribed ? (
              <Bell className="w-4 h-4 text-purple-500" />
            ) : (
              <BellOff className="w-4 h-4 text-gray-400" />
            )}
            <Switch 
              checked={subscribed} 
              onCheckedChange={handleToggleSubscription}
              className="data-[state=checked]:bg-purple-500"
            />
          </div>
        </div>
        {preview && (
          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {preview.period_start} - {preview.period_end}
          </p>
        )}
      </CardHeader>
      
      <CardContent className="space-y-4">
        {preview && (
          <>
            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-white rounded-lg p-2 text-center shadow-sm border border-purple-100">
                <div className="text-lg font-bold text-purple-700">
                  {preview.stats.current_week.scans}
                </div>
                <div className="text-[10px] text-gray-500 flex items-center justify-center gap-1">
                  {t('digest.scans', 'Scans')}
                  {getTrendIcon(preview.stats.changes.scans_percent)}
                </div>
              </div>
              <div className="bg-white rounded-lg p-2 text-center shadow-sm border border-purple-100">
                <div className="text-lg font-bold text-indigo-700">
                  {preview.stats.current_week.rshds}
                </div>
                <div className="text-[10px] text-gray-500 flex items-center justify-center gap-1">
                  {t('digest.deals', 'Deals')}
                  {getTrendIcon(preview.stats.changes.rshds_percent)}
                </div>
              </div>
              <div className="bg-white rounded-lg p-2 text-center shadow-sm border border-purple-100">
                <div className="text-lg font-bold text-green-700">
                  ${preview.stats.current_week.revenue.toFixed(0)}
                </div>
                <div className="text-[10px] text-gray-500">
                  {t('digest.revenue', 'Revenue')}
                </div>
              </div>
            </div>

            {/* Recommendations Preview */}
            {preview.recommendations && preview.recommendations.length > 0 && (
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                <div className="flex items-center gap-1.5 text-xs font-medium text-amber-800 mb-1">
                  <Sparkles className="w-3 h-3" />
                  {t('digest.recommendation', 'Tip')}
                </div>
                <p className="text-xs text-amber-700 line-clamp-2">
                  {preview.recommendations[0].replace(/^[^\s]+\s*/, '')}
                </p>
              </div>
            )}

            {/* Preview Button */}
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="w-full text-xs text-purple-600 hover:text-purple-800 flex items-center justify-center gap-1 py-1"
              data-testid="toggle-digest-preview"
            >
              <Eye className="w-3 h-3" />
              {showPreview 
                ? t('digest.hide_preview', 'Hide Preview') 
                : t('digest.show_preview', 'Show Email Preview')}
            </button>

            {/* Email Preview */}
            {showPreview && (
              <div 
                className="border rounded-lg overflow-hidden bg-white max-h-64 overflow-y-auto text-xs"
                dangerouslySetInnerHTML={{ __html: preview.html_preview }}
              />
            )}
          </>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            onClick={handleSendNow}
            disabled={sending || !subscribed}
            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-xs h-9"
            data-testid="send-digest-now-btn"
          >
            {sending ? (
              <Loader2 className="w-3 h-3 animate-spin mr-1" />
            ) : (
              <Send className="w-3 h-3 mr-1" />
            )}
            {t('digest.send_now', 'Send Now')}
          </Button>
        </div>

        {/* Schedule Info */}
        <p className="text-[10px] text-gray-400 text-center">
          {subscribed 
            ? t('digest.schedule_info', 'Auto-sends every Sunday at 9 AM')
            : t('digest.paused', 'Digest emails paused')}
        </p>
      </CardContent>
    </Card>
  );
}
